import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  SmartToy,
  MenuBook,
  Business,
  Mail,
  RateReview,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  LinkedIn as LinkedInIcon,
} from "@mui/icons-material";
import apiClient from "@/api/client";
import { PIPELINE_LABELS, PIPELINE_COLORS, type PipelineStage } from "@/types";
import { brand } from "@/theme";
import AgendaWidget from "@/components/AgendaWidget";

interface DashboardSummary {
  agents: { total: number; active: number };
  kb: { total: number; published: number; drafts: number; with_embeddings: number };
  crm: { companies: number; contacts: number; active_followups: number; overdue_followups: number };
  pipeline: Record<string, number>;
  messages: { unread: number; reviewed: number; archived: number; high_priority: number };
  pending_suggestions: number;
}

interface SystemHealth {
  overall: string;
  checks: Array<{ name: string; status: string; detail: string }>;
}

interface LinkedInCadence {
  days_since_last_post: number | null;
  posts_this_week: number;
  cadence_target_per_week: number;
  on_track: boolean;
  last_post_at: string | null;
}

function StatCard({
  icon,
  label,
  value,
  sub,
  color,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  color?: string;
  onClick?: () => void;
}) {
  const content = (
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
      <Box sx={{ color: color ?? brand.forestGreen }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h4" fontWeight={600} sx={{ color: color ?? "text.primary" }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </CardContent>
  );

  if (onClick) {
    return (
      <Card>
        <CardActionArea onClick={onClick}>{content}</CardActionArea>
      </Card>
    );
  }
  return <Card>{content}</Card>;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  healthy: <CheckCircle sx={{ color: "#4CAF50" }} />,
  warning: <Warning sx={{ color: "#FF9800" }} />,
  unhealthy: <ErrorIcon sx={{ color: "#f44336" }} />,
};

export default function Dashboard() {
  const navigate = useNavigate();

  const { data: summary, isLoading } = useQuery<DashboardSummary>({
    queryKey: ["dashboard-summary"],
    queryFn: async () => {
      const res = await apiClient.get<DashboardSummary>("/dashboard/summary");
      return res.data;
    },
    refetchInterval: 60000,
  });

  const { data: health } = useQuery<SystemHealth>({
    queryKey: ["system-health"],
    queryFn: async () => {
      const res = await apiClient.get<SystemHealth>("/dashboard/system-health");
      return res.data;
    },
    refetchInterval: 60000,
  });

  const { data: cadence } = useQuery<LinkedInCadence>({
    queryKey: ["linkedin-cadence"],
    queryFn: async () => {
      const res = await apiClient.get<LinkedInCadence>("/dashboard/linkedin-cadence");
      return res.data;
    },
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) {
    return <Alert severity="error">Failed to load dashboard data.</Alert>;
  }

  // Defensive defaults — every nested block of the response can be missing
  // if a sub-query in /dashboard/summary returns no rows (dict(... or {}) on
  // the backend produces an empty dict that loses sub-fields). Render
  // zeros instead of crashing the whole admin.
  const agents = summary.agents ?? { total: 0, active: 0 };
  const kb = summary.kb ?? { total: 0, published: 0, drafts: 0, with_embeddings: 0 };
  const crm = summary.crm ?? {
    companies: 0,
    contacts: 0,
    active_followups: 0,
    overdue_followups: 0,
  };
  const pipeline = summary.pipeline ?? {};
  const messages = summary.messages ?? { unread: 0, reviewed: 0, archived: 0, high_priority: 0 };
  const pendingSuggestions = summary.pending_suggestions ?? 0;

  const activePipeline = Object.entries(pipeline)
    .filter(([s]) => !["won", "lost", "dormant"].includes(s))
    .reduce((sum, [, c]) => sum + c, 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Mastermind platform overview for Elite Mind.
      </Typography>

      {/* Agenda — unified today/week/month view across CRM, pipeline, finance */}
      <Box sx={{ mb: 3 }}>
        <AgendaWidget />
      </Box>

      {/* LinkedIn cadence — surfaces whether Evelina's posting cadence is
          on track for the LinkedIn growth loop. Subtle alert variant so
          "on track" is reassuring rather than nagging. */}
      {cadence && (
        <Alert
          icon={<LinkedInIcon fontSize="inherit" />}
          severity={cadence.on_track ? "success" : "warning"}
          sx={{ mb: 3, cursor: "pointer" }}
          onClick={() => navigate("/kb?category=linkedin_post")}
        >
          {cadence.days_since_last_post === null
            ? "No LinkedIn posts tracked yet — draft your first one in the KB to start the cadence."
            : `You posted ${cadence.posts_this_week} time(s) this week (target: ${cadence.cadence_target_per_week}). Last post: ${cadence.days_since_last_post} day(s) ago.`}
        </Alert>
      )}

      {/* Top stat cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          icon={<SmartToy fontSize="large" />}
          label="Active Agents"
          value={agents.active}
          sub={`of ${agents.total}`}
          onClick={() => navigate("/agents")}
        />
        <StatCard
          icon={<MenuBook fontSize="large" />}
          label="KB Articles"
          value={kb.total}
          sub={`${kb.published} published`}
          onClick={() => navigate("/kb")}
        />
        <StatCard
          icon={<Business fontSize="large" />}
          label="Companies"
          value={crm.companies}
          sub={`${activePipeline} in pipeline`}
          onClick={() => navigate("/crm")}
        />
        <StatCard
          icon={<Mail fontSize="large" />}
          label="Unread Messages"
          value={messages.unread}
          sub={messages.high_priority > 0 ? `${messages.high_priority} high priority` : undefined}
          color={messages.high_priority > 0 ? "#E65100" : undefined}
          onClick={() => navigate("/agents")}
        />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {/* Pipeline mini-board */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sales Pipeline
            </Typography>
            {Object.entries(PIPELINE_LABELS).map(([stage, label]) => {
              const count = pipeline[stage] ?? 0;
              const maxCount = Math.max(...Object.values(pipeline), 1);
              return (
                <Box key={stage} sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {count}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(count / maxCount) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      bgcolor: "#eee",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: PIPELINE_COLORS[stage as PipelineStage],
                      },
                    }}
                  />
                </Box>
              );
            })}
            {crm.overdue_followups > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {crm.overdue_followups} overdue follow-up(s)
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* System health */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                System Health
              </Typography>
              {health && (
                <Chip
                  label={health.overall}
                  size="small"
                  color={
                    health.overall === "healthy"
                      ? "success"
                      : health.overall === "warning"
                        ? "warning"
                        : "error"
                  }
                />
              )}
            </Box>
            {(health?.checks ?? []).map((check) => (
              <Box
                key={check.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "#fafafa",
                }}
              >
                {STATUS_ICONS[check.status] ?? STATUS_ICONS.warning}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {check.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {check.detail}
                  </Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Attention Needed
            </Typography>
            {pendingSuggestions > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: "#FFF3E0",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 1 },
                }}
                onClick={() => navigate("/admin/suggestions")}
              >
                <RateReview sx={{ color: brand.warmGold }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {pendingSuggestions} KB suggestion(s) pending review
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to review
                  </Typography>
                </Box>
              </Box>
            )}
            {crm.overdue_followups > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: "#FFF3E0",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 1 },
                }}
                onClick={() => navigate("/crm")}
              >
                <Warning sx={{ color: "#E65100" }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {crm.overdue_followups} overdue follow-up(s)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to view CRM
                  </Typography>
                </Box>
              </Box>
            )}
            {messages.high_priority > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: "#FFEBEE",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 1 },
                }}
                onClick={() => navigate("/agents")}
              >
                <Mail sx={{ color: "#f44336" }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {messages.high_priority} high-priority agent message(s)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to view agent comms
                  </Typography>
                </Box>
              </Box>
            )}
            {pendingSuggestions === 0 &&
              crm.overdue_followups === 0 &&
              messages.high_priority === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nothing needs attention right now.
                </Typography>
              )}
          </CardContent>
        </Card>

        {/* KB health */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Knowledge Base
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              {[
                { label: "Total", value: kb.total },
                { label: "Published", value: kb.published },
                { label: "Drafts", value: kb.drafts },
                { label: "With Embeddings", value: kb.with_embeddings },
              ].map((s) => (
                <Box
                  key={s.label}
                  sx={{ textAlign: "center", p: 1, borderRadius: 1, bgcolor: "#fafafa" }}
                >
                  <Typography variant="h5" fontWeight={600}>
                    {s.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            {kb.total > 0 && kb.with_embeddings < kb.total && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {kb.total - kb.with_embeddings} article(s) missing embeddings — run KB Agent
                heartbeat
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
